import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  saveProjectInfo, saveQuestionnaire, suggestProjectInfo,
  type ConfirmedProfile, type ProfileSuggestion,
} from '../../api/client';
import { useAuditStore } from '../../stores/useAuditStore';
import { useAuthStore } from '../../stores/useAuthStore';
import {
  readConfirmDraft, useOnboardingStore, writeConfirmDraft,
} from '../../stores/useOnboardingStore';
import { useStageGuard } from '../../hooks/useStageGuard';
import OnboardingFooter from './OnboardingFooter';
import SelectField, { type SelectOption } from './SelectField';
import TagField from './TagField';
import './onboarding.css';

/**
 * Step 4 — confirm what we found.
 *
 * Everything here arrived from the Collect pass; the user's job is to correct
 * it, not to fill it in. Three further fields the model inferred
 * (business_stage, revenue_range, success_goal) are deliberately NOT shown —
 * they feed the questionnaire that the strategy agent and report compiler
 * read, and asking about them would break the promise this flow makes. They
 * are saved alongside the visible answers.
 */

const BUSINESS_MODELS: SelectOption[] = [
  { value: 'saas', label: 'SaaS / Subscriptions' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'mobile_app', label: 'Mobile App' },
  { value: 'b2b', label: 'B2B Services' },
  { value: 'other', label: 'Other' },
];

/** "a, b and c" — a list a person reads aloud, not one joined by commas. */
function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const REVENUE_MODELS: SelectOption[] = [
  { value: 'monthly_subscriptions', label: 'Monthly Subscriptions' },
  { value: 'annual_subscriptions', label: 'Annual Subscriptions' },
  { value: 'one_time_purchase', label: 'One-time Purchase' },
  { value: 'freemium', label: 'Freemium / Usage-based' },
  { value: 'enterprise_contracts', label: 'Enterprise Contracts' },
  { value: 'transaction_fees', label: 'Transaction Fees' },
];

const CHALLENGES: SelectOption[] = [
  'Revenue plateau or decline', 'Customer churn', 'Rising acquisition costs',
  'Data & attribution gaps', 'Pricing strategy', 'Market expansion',
].map((c) => ({ value: c, label: c }));

/** The collection chat Collect stamped, for when this screen is reloaded. */
function readStashedCollectChatId(): string | null {
  try {
    return sessionStorage.getItem('collect_chat_id');
  } catch {
    return null;
  }
}

/** Where the values on screen came from. The banner and the revenue hint may
 *  only claim what that source supports (VIBE-2035): a restored draft or a
 *  confirmed profile is the user's own input, not something we detected. */
type Shown = 'loading' | 'failed' | 'draft' | 'confirmed' | 'inferred';

function bannerText(shown: Shown, s: ProfileSuggestion | null): [string, string] {
  switch (shown) {
    case 'loading':
      return ['Reading your connected data…', ''];
    case 'failed':
      return ["We couldn't fill this in automatically.", 'Enter the details below.'];
    case 'draft':
      return ['Your unsaved changes.', 'Restored from your last visit to this step.'];
    case 'confirmed':
      return ['Your confirmed details.', 'Showing what you confirmed for this audit.'];
    case 'inferred':
      return [
        'Filled in from your connected data.',
        s?._sources?.length ? `Read from ${joinList(s._sources)}.` : 'Nothing here was entered by hand.',
      ];
  }
}

function revenueHint(shown: Shown, s: ProfileSuggestion | null): string {
  if (shown !== 'inferred' || !s) return 'Pick everything that applies.';
  if (s._revenue_set_from) {
    return `Set from your ${s._revenue_set_from} store. Remove anything that doesn't apply.`;
  }
  if (s._revenue_evidence?.length) {
    return `Detected from your ${joinList(s._revenue_evidence)} data. Remove anything that doesn't apply.`;
  }
  return 'No billing source is connected, so this is our best guess. Pick what applies.';
}

export default function ConfirmView() {
  const navigate = useNavigate();
  useStageGuard('/confirm');

  const authEmail = useAuthStore((s) => s.user?.email ?? '');
  const storeCollectChatId = useAuditStore((s) => s.collectChatId);
  // The chat onboarding is building — collection filled its source_data, and
  // the audit will run in it too, so a questionnaire saved here is already
  // attached to the run that compiles the report. Falls back to sessionStorage
  // so a reload doesn't detach the confirmed profile from the collected data.
  const chatId = storeCollectChatId ?? readStashedCollectChatId();
  const suggestion = useOnboardingStore((s) => s.suggestion);
  const setSuggestion = useOnboardingStore((s) => s.setSuggestion);
  const setConfirmedAnswers = useOnboardingStore((s) => s.setConfirmedAnswers);
  const setConfirmedProfile = useOnboardingStore((s) => s.setConfirmedProfile);

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [businessModel, setBusinessModel] = useState('');
  const [revenueModels, setRevenueModels] = useState<string[]>([]);
  const [challenges, setChallenges] = useState<string[]>([]);
  const [shown, setShown] = useState<Shown>('loading');
  const loading = shown === 'loading';
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Set by the first user edit. Until then the form only shows the suggestion,
  // and storing that as a "draft" would hide a fresher one after a re-collect.
  const editedRef = useRef(false);
  const hydratedRef = useRef(false);
  // Inferred but never shown — carried through to the questionnaire.
  const hiddenRef = useRef({ business_stage: '', revenue_range: '', success_goal: '' });

  const apply = useCallback((s: ConfirmedProfile) => {
    setName(s.name || '');
    setUrl(s.website_url || '');
    setDescription(s.description || '');
    setBusinessModel(s.business_model || '');
    setRevenueModels(s.revenue_models || []);
    setChallenges(s.challenges || []);
    hiddenRef.current = {
      business_stage: s.business_stage || '',
      revenue_range: s.revenue_range || '',
      success_goal: s.success_goal || '',
    };
  }, []);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    // Unsaved edits win: Back to /connect and a reload must bring the user
    // back to what they typed, not to what the model suggested (VIBE-2035).
    const draft = readConfirmDraft(chatId);
    if (draft) {
      editedRef.current = true;
      apply(draft);
      setShown('draft');
      return;
    }

    if (suggestion) {
      apply(suggestion);
      setShown(suggestion._confirmed ? 'confirmed' : 'inferred');
      return;
    }

    // Landed here directly (a refresh, or a bookmark). The server returns what
    // was already confirmed for this chat, else a fresh inference. On failure
    // the form stays blank for the user to fill — never another business's
    // stored details.
    suggestProjectInfo(chatId)
      .then((fresh) => {
        setSuggestion(fresh);
        apply(fresh);
        setShown(fresh._confirmed ? 'confirmed' : 'inferred');
      })
      .catch(() => setShown('failed'));
  }, [suggestion, chatId, apply, setSuggestion]);

  const currentProfile = useCallback((): ConfirmedProfile => ({
    name,
    website_url: url,
    description,
    business_model: businessModel,
    revenue_models: revenueModels,
    challenges,
    ...hiddenRef.current,
  }), [name, url, description, businessModel, revenueModels, challenges]);

  useEffect(() => {
    if (!loading && editedRef.current) writeConfirmDraft(chatId, currentProfile());
  }, [loading, chatId, currentProfile]);

  /** Wraps a setter so the first real edit starts the draft. */
  const edit = useCallback(<T,>(setter: (value: T) => void) => (value: T) => {
    editedRef.current = true;
    setter(value);
  }, []);

  const submit = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    const profile = currentProfile();
    try {
      await saveProjectInfo({
        name,
        description,
        website_url: url,
        email: authEmail,
        logo_path: null,
        product_types: [],
        business_model: businessModel,
        revenue_models: revenueModels,
      });
      // Keep the store in step so the analysis screen and report show the
      // confirmed name rather than a stale one.
      const store = useAuditStore.getState();
      store.setProjectField('projectName', name);
      store.setProjectField('projectDescription', description);
      store.setProjectField('projectUrl', url);
      store.setProjectField('projectEmail', authEmail);

      const answers: Record<string, string | string[]> = {
        business_model: BUSINESS_MODELS.find((m) => m.value === businessModel)?.label ?? '',
        revenue_model: REVENUE_MODELS.find((m) => m.value === revenueModels[0])?.label ?? '',
        challenges,
        ...hiddenRef.current,
      };
      // Empty answers would overwrite real ones with blanks on a re-save.
      for (const key of Object.keys(answers)) {
        const value = answers[key];
        if (!value || (Array.isArray(value) && value.length === 0)) delete answers[key];
      }
      // Saved against the COLLECTION chat, which ProcessingView adopts as the
      // audit chat. The analysis reads this profile and these answers, so a
      // failed save must stop here — not start an audit of whatever was
      // stored before (VIBE-2035).
      if (chatId) await saveQuestionnaire(chatId, answers, profile);
      setConfirmedAnswers(answers);
      setConfirmedProfile(profile);
      // Coming back to this screen in the same tab (a failed start, browser
      // Back) must show what was just confirmed, not the original suggestion.
      setSuggestion({ ...profile, _sources: [], _reasoning: '', _confirmed: true });
      writeConfirmDraft(chatId, null);
    } catch {
      setSaveError("We couldn't save your changes. Check your connection and try again.");
      return;
    } finally {
      setSaving(false);
    }
    // No chat id in the URL on purpose: ProcessingView adopts the collection
    // chat (or mints one) and passes it along as `_source_chat_id`.
    navigate('/processing');
  }, [
    saving, currentProfile, name, description, url, authEmail, businessModel, revenueModels,
    challenges, chatId, navigate, setConfirmedAnswers, setConfirmedProfile, setSuggestion,
  ]);

  const [bannerLead, bannerDetail] = bannerText(shown, suggestion);

  return (
    <>
      <div className="mx-auto max-w-[1180px] px-4 pb-24 pt-8 sm:px-8">
        <h1 className="mb-1 text-xl font-bold tracking-tight text-foreground">
          Confirm what we found
        </h1>
        <p className="mb-6 text-sm text-muted-foreground">
          Everything below was filled in from your connected data. Correct anything that looks off.
        </p>

        <div className="mb-6 flex items-start gap-3 rounded-xl border bg-card px-4 py-3.5">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-secondary" />
          <p className="text-[12.5px] leading-relaxed text-muted-foreground">
            <b className="font-semibold text-foreground">{bannerLead}</b>
            {bannerDetail && ` ${bannerDetail}`}
          </p>
        </div>

        <div className={cn('grid gap-5 sm:grid-cols-2 sm:gap-x-6', loading && 'opacity-50')}>
          <Field id="cf-name" label="Name">
            <ClearableInput id="cf-name" value={name} onChange={edit(setName)} placeholder="Business name" />
          </Field>
          <Field id="cf-url" label="Website URL">
            <ClearableInput id="cf-url" value={url} onChange={edit(setUrl)} placeholder="https://example.com" />
          </Field>

          <Field id="cf-desc" label="Description" className="sm:col-span-2">
            <textarea
              id="cf-desc"
              rows={3}
              value={description}
              onChange={(e) => edit(setDescription)(e.target.value)}
              placeholder="What the business sells, to whom, and how it makes money."
              className="w-full resize-y rounded-lg border bg-muted/40 px-3 py-2 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus:border-ring focus:bg-background focus:outline-none focus:ring-[3px] focus:ring-ring/20"
            />
          </Field>

          <Field label="Business model">
            <SelectField
              ariaLabel="Business model"
              value={businessModel}
              options={BUSINESS_MODELS}
              onChange={edit(setBusinessModel)}
            />
          </Field>

          <Field label="Revenue model" className="sm:col-span-2">
            <TagField
              ariaLabel="Revenue model"
              values={revenueModels}
              options={REVENUE_MODELS}
              onChange={edit(setRevenueModels)}
            />
            <p className="text-xs text-muted-foreground">
              {revenueHint(shown, suggestion)}
            </p>
          </Field>

          <Field label="Biggest business challenges" className="sm:col-span-2">
            <TagField
              ariaLabel="Biggest business challenges"
              values={challenges}
              options={CHALLENGES}
              onChange={edit(setChallenges)}
            />
            <p className="text-xs text-muted-foreground">
              We prioritise recommendations against these.
            </p>
          </Field>
        </div>
      </div>

      {saveError && (
        <p role="alert" className="mx-auto max-w-[1180px] px-4 pb-4 text-sm text-destructive sm:px-8">
          {saveError}
        </p>
      )}

      <OnboardingFooter
        step={4}
        continueLabel={saving ? 'Saving…' : 'Start analysis'}
        continueDisabled={loading || saving}
        onContinue={submit}
        onBack={() => navigate('/connect')}
      />
    </>
  );
}

function Field({
  id, label, className, children,
}: {
  id?: string;
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id} className="text-[13px] font-semibold">{label}</Label>
      {children}
    </div>
  );
}

/** Text input with the design's hover-revealed clear button. */
function ClearableInput({
  id, value, placeholder, onChange,
}: {
  id: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="group relative">
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bg-muted/40 pr-9"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear field"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 flex size-[22px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-focus-within:opacity-100 group-hover:opacity-100"
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}
